<template>
    <div class="account-page-padding">
        <div class="favorites">
            <div class="header">
                <h1>Favorites</h1>
                <h3 v-if="favoritesCount !== null" id="favorites_count">{{ favoritesCount }} favorites</h3>
                <details ref="sortOptionDropdown" class="sort-container mobile-hidden">
                    <summary class="sort-button" aria-label="Sort products">
                        Sort: {{ sortOption?.label }}
                    </summary>
                    <ul class="sort-content">
                        <li v-for="(option, i) in sortOptions" :id="`favorites_sort_${i}`" :key="i" class="sort-select paragraph-default">
                            <a @click.prevent="changeSort(option)" href="#" :aria-label="option.label">{{ option.label }}</a>
                        </li>
                    </ul>
                </details>
            </div>
            <FavoriteTile
                v-for="(favorite, index) in favoritesMapped"
                :key="index"
                :favorite="favorite"
                :id="`favorite_tile_${index}`"
                style="margin-bottom: 1rem;"
                @removeFavorite="removeFavorite($event)"
            />
            <pagination
                ref="pagination"
                v-if="(props.favorites?.meta?.total || 0) > limit"
                :pagination="{
                    totalItems: props.favorites?.meta?.total || 0,
                    currentPage: props.favorites?.meta?.current_page,
                    pageSize: limit,
                    maxPages: props.favorites?.meta.last_page || 1
                }"
                @pageChange="(page) => to({ newParams: { page } })"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
    import { ref, computed, onMounted, inject } from "vue";
    import type { PropType } from "vue";
    import FavoriteTile from "@/components/FavoriteTile.vue";
    import { router } from "@inertiajs/vue3";
    import { useRouter } from "@/helpers/composables/useRouter";
    import { onClickOutside } from '@vueuse/core'
    import { useSearchByTerm } from "@/helpers/composables/searchSpringApi";
    import SearchSpringApi from "@/helpers/api/SearchSpringAPI.ts";
    import Account from "@/Shared/Account.vue";
    import Pagination from "@/components/Pagination.vue";
    import DeleteFavoriteModal from "@/components/DeleteFavoriteModal.vue"
    import { watchDeep } from "@vueuse/core";
    import { useDialog } from "primevue/usedialog";

    let props = defineProps({
        favorites: {
            type: Object,
            required: true,
            default: () => {
                return {};
            }
        }
    });
    let route = inject("route");

    let limit = 4;

    let sortOption = ref("");
    let sortOptionDropdown = ref(null);

    let sortOptions = [
        { label: "Newest First", value: "created_at:desc" },
        { label: "Oldest First", value: "created_at:asc" }
    ];

    onClickOutside(sortOptionDropdown, event => sortOptionDropdownToggle(false));

    const sortOptionDropdownToggle = (val)=>{
        return sortOptionDropdown.value.open = (val === undefined) ? !sortOptionDropdown.value?.open : val;
    }
    const changeSort = (event) => {
        sortOption.value = event;
        sortOptionDropdownToggle(false);
        to({ newParams: { orderBy: sortOption.value.value }, options: {preserveState: true, preserveScroll: true}});
    };

    const favoritesCount = computed(()=>{
        return props.favorites?.meta?.total ?? null;
    })

    let { to } = useRouter({
        router,
        route
    });

    onMounted(() => {
        sortOption.value =
            sortOptions.find((opt) => opt.value == route().params["orderBy"]) ??
            sortOptions[0];
    });

    const searchSpringApi = new SearchSpringApi();
    let { data: searchSpringData, handleFilter } = useSearchByTerm({ searchSpringApi: searchSpringApi });

    let favoriteIds = computed(()=>{
        return props.favorites.data.map((favorite) => favorite.item_index);
    })

    function setFavorites(){
        handleFilter(
            favoriteIds.value.map((id) => {
                return{
                    facet: {
                        field: 'product_id',
                        multiple: true,
                    },
                    value: {
                        value: id
                    },
                }
            })
        )
    }

    watchDeep(favoriteIds, () => {
        setFavorites();
    }, {
        immediate: true
    });

    const favoritesMapped = computed(()=>{
        return props.favorites.data.map(favorite=>{
            return {
                ...favorite,
                product: searchSpringData.value.find(product=>(product.product_id == favorite.item_index))
            }
        })
    })

    let dialog = useDialog();
    const removeFavorite = (id) => {
        dialog.open(DeleteFavoriteModal, {
            props: {
                modal: true,
                showHeader: false
            },
            data: {
                favorite_id: id,
                afterSubmit: ()=>{
                    let urlParams = new URLSearchParams(window.location.search);
                    let page = urlParams.get('page');
                    to({
                        newParams: {...(page ? {page} : {})},
                        options: { preserveScroll: true, preserveState: true, only: ['favorites'] }
                    })
                }
            }
        });
    };
</script>
